/**
 * Gobodo Object Type Diagnostics
 * 
 * Utility to monitor and identify missing object types from API calls.
 * Can be used in browser console or integrated into error handling.
 */

import { objectTypeExists } from './gobodoObjectTypeVerifier';
import { USE_GOBODO_API } from './gobodoUtils';

// Track missing object types encountered during runtime
const missingObjectTypes = new Set<string>();
const attemptedOperations = new Map<string, { operation: string; timestamp: Date; error?: string }[]>();

/**
 * Log a missing object type for later analysis
 */
export function logMissingObjectType(
  objectType: string,
  operation: string,
  error?: string
): void {
  if (!USE_GOBODO_API) return;
  
  missingObjectTypes.add(objectType);
  
  if (!attemptedOperations.has(objectType)) {
    attemptedOperations.set(objectType, []);
  }
  
  attemptedOperations.get(objectType)!.push({
    operation,
    timestamp: new Date(),
    error
  });
  
  console.warn(`[Gobodo Diagnostics] Missing object type detected: ${objectType}`, {
    operation,
    error,
    timestamp: new Date().toISOString()
  });
}

/**
 * Get all missing object types that were detected
 */
export function getMissingObjectTypes(): Array<{
  objectType: string;
  attempts: Array<{ operation: string; timestamp: Date; error?: string }>;
}> {
  return Array.from(missingObjectTypes).map(objectType => ({
    objectType,
    attempts: attemptedOperations.get(objectType) || []
  }));
}

/**
 * Clear the missing object types log
 */
export function clearMissingObjectTypes(): void {
  missingObjectTypes.clear();
  attemptedOperations.clear();
}

/**
 * Check if an object type exists and log if missing
 */
export async function checkObjectTypeExists(
  objectType: string,
  operation: string
): Promise<boolean> {
  try {
    const exists = await objectTypeExists(objectType);
    if (!exists) {
      logMissingObjectType(objectType, operation, 'Object type not found');
    }
    return exists;
  } catch (error: any) {
    logMissingObjectType(objectType, operation, error.message);
    return false;
  }
}

/**
 * Generate a report of missing object types with suggested creation code
 */
export function generateMissingObjectTypesReport(): string {
  const missing = getMissingObjectTypes();
  
  if (missing.length === 0) {
    return '✅ No missing object types detected.';
  }
  
  let report = `\n📋 Missing Object Types Report (${missing.length} found)\n`;
  report += '='.repeat(60) + '\n\n';
  
  missing.forEach(({ objectType, attempts }) => {
    report += `❌ ${objectType}\n`;
    report += `   Operations attempted: ${attempts.length}\n`;
    report += `   First seen: ${attempts[0]?.timestamp.toISOString()}\n`;
    report += `   Operations:\n`;
    attempts.forEach(attempt => {
      report += `     - ${attempt.operation} at ${attempt.timestamp.toISOString()}\n`;
      if (attempt.error) {
        report += `       Error: ${attempt.error}\n`;
      }
    });
    
    // Generate suggested creation code
    const baseName = objectType.replace(/__c$/, '').replace(/_/g, ' ');
    report += `\n   Suggested creation:\n`;
    report += `   await ensureObjectTypeExists('${objectType}', {\n`;
    report += `     apiName: '${objectType}',\n`;
    report += `     label: '${baseName}',\n`;
    report += `     pluralLabel: '${baseName}s',\n`;
    report += `     description: 'Auto-created: ${objectType}',\n`;
    report += `     icon: 'Package',\n`;
    report += `     category: 'custom',\n`;
    report += `   });\n\n`;
  });
  
  return report;
}

/**
 * Print missing object types report to console
 */
export function printMissingObjectTypesReport(): void {
  console.log(generateMissingObjectTypesReport());
}

// Make functions available globally in development
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  // Register existing diagnostics
  (window as any).gobodoDiagnostics = {
    getMissingObjectTypes,
    clearMissingObjectTypes,
    generateMissingObjectTypesReport,
    printMissingObjectTypesReport,
    checkObjectTypeExists
  };
  
  // Register API diagnostic tool
  import('./gobodoApiDiagnostic').then(m => m.registerGobodoApiDiagnostic()).catch(() => {});

  console.log(
    '%c[Gobodo Diagnostics] Available in console:',
    'color: #6366f1; font-weight: bold;'
  );
  console.log('  window.gobodoDiagnostics.getMissingObjectTypes()');
  console.log('  window.gobodoDiagnostics.printMissingObjectTypesReport()');
  console.log('  window.gobodoDiagnostics.clearMissingObjectTypes()');
  console.log('  window.__GOBODO_DEBUG_RECORDS__ = true  // enable verbose getGobodoRecords/convert logs');
  console.log('  window.gobodoApiTest()  // run full API diagnostic');
}
